/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Wizard application components
import CompetitionsTable from "views/competitions-table";

import TableContainer from "@mui/material/TableContainer";
import { Button, Grid } from "@mui/material";

function Competitions({
  provider,
  template,
  initData,
  filterSportData,
  filterCompetitionData,
  handleStepperButtonStatus,
  handleStepData,
  showSportPromosSwitch,
  showCompetitionPromosSwitch,
}) {
  const [sports, setSports] = useState([]);
  const [selectedSport, setSelectedSport] = useState("");
  const [selectedSportName, setSelectedSportName] = useState("");
  const [competitionFilters, setCompetitionFilters] = useState("all");
  const [competitions, setCompetitions] = useState(
    initData.filter((competition) => competition.sport === selectedSportName)
  );
  const [advancedView, setAdvancedView] = useState(false);

  const selectedSportData = sports.find((sport) => sport.id === selectedSport);
  const selectedSportPromosEnabled = selectedSportData ? !!selectedSportData.promos : false;
  const canViewSportPromoInfo = !!showSportPromosSwitch;

  function handleAdvancedView(e) {
    e.preventDefault();
    let view = advancedView;
    setAdvancedView(!view);
    setCompetitionFilters("all");
  }

  function createCompetition(
    id: number,
    sport: string,
    region: string,
    competition: string,
    pregame: boolean,
    inplay: boolean,
    both: boolean
  ) {
    return { id, sport, region, competition, pregame, inplay, both };
  }

  async function renderCompetitions(sportName) {
    const filteredCompetitions = competitions.filter(
      (competition) => competition.sport === sportName
    );
    setCompetitions([]);
    setCompetitions(filteredCompetitions);
  }

  const handleSportChange = (e) => {
    e.preventDefault();
    //console.log(e.currentTarget.value);
    const sportId = parseInt(e.currentTarget.value);
    selectSport(sportId);
  };

  function selectSport(sportId) {
    const sport = sports.find((sport) => {
      return sport.id === sportId;
    });
    if (!sport) return;
    sport.selected = true;

    setSelectedSport(sportId);
    setSelectedSportName(sport.name);
    renderCompetitions(sport.name);
  }

  function filterCompetitions(e, filter) {
    e.preventDefault();
    setCompetitionFilters(filter);
  }

  useEffect(() => {
    setSports(filterSportData);
    setSelectedSport(filterSportData[0].id);
    setSelectedSportName(filterSportData[0].name);
    renderCompetitions(filterSportData[0].name);
  }, [filterSportData]);

  useEffect(() => {
    if (filterCompetitionData) setCompetitionFilters("all");
  }, [filterCompetitionData]);

  return (
    <SoftBox>
      <SoftBox width="80%" textAlign="center" mx="auto" mb={4}>
        <SoftBox mb={1}>
          <SoftTypography variant="h5" fontWeight="regular">
            Now its time to select the Competitions per Sport you want
          </SoftTypography>
        </SoftBox>
        <SoftTypography variant="body2" fontWeight="regular" color="text">
          Let us know which Competitions in any Sport you want to deliver to you
        </SoftTypography>
      </SoftBox>
      <SoftBox mt={2}>
        {sports.map((sport) => (
          <SoftButton
            key={sport.id}
            variant="contained"
            color={selectedSport === sport.id ? "info" : sport.is_esport ? "light" : "light"}
            size="small"
            sx={{
              marginLeft: "5px",
              marginBottom: "5px",
              minWidth: "140px",
              padding: "0px !important",
            }}
            value={sport.id}
            onClick={handleSportChange}
          >
            {sport.name}
          </SoftButton>
        ))}
      </SoftBox>
      <SoftBox mt={2}>
        <TableContainer id="sports-selected-table">
          {canViewSportPromoInfo ? (
            <Grid
              style={{
                textAlign: "right",
                maxWidth: "97% !important",
                width: "97%",
                display: "flex",
              }}
            >
              <Grid style={{ width: "100%", textAlign: "right" }}>
                <SoftTypography
                  variant="button"
                  fontWeight="bold"
                  color={selectedSportPromosEnabled ? "success" : "secondary"}
                  sx={{ marginRight: "14px" }}
                >
                  {`Sport Promo: ${selectedSportPromosEnabled ? "ON" : "OFF"}`}
                </SoftTypography>
              </Grid>
            </Grid>
          ) : null}
          <Grid
            style={{
              textAlign: "right",
              maxWidth: "97% !important",
              width: "97%",
              display: "flex",
            }}
          >
            <Grid style={{ width: "70%", textAlign: "left" }}>
              <Button
                sx={{ color: competitionFilters == "all" ? "#cb0c9f !important" : "#17c1e8" }}
                onClick={(e) => filterCompetitions(e, "all")}
              >
                All
              </Button>
              <Button
                sx={{ color: competitionFilters == "selected" ? "#cb0c9f !important" : "#17c1e8" }}
                onClick={(e) => filterCompetitions(e, "selected")}
              >
                Selected
              </Button>
              <Button
                sx={{ color: competitionFilters == "blocked" ? "#cb0c9f !important" : "#17c1e8" }}
                disabled={filterCompetitionData.length == 0}
                onClick={(e) => filterCompetitions(e, "blocked")}
              >
                Blocked
              </Button>
              <Button
                sx={{ color: competitionFilters == "pregameon" ? "#cb0c9f !important" : "#17c1e8" }}
                disabled={filterCompetitionData.length == 0}
                onClick={(e) => filterCompetitions(e, "pregameon")}
              >
                Pregame On
              </Button>
              <Button
                sx={{ color: competitionFilters == "inplayon" ? "#cb0c9f !important" : "#17c1e8" }}
                disabled={filterCompetitionData.length == 0}
                onClick={(e) => filterCompetitions(e, "inplayon")}
              >
                Inplay On
              </Button>
              <Button
                sx={{
                  display: advancedView ? "" : "none",
                  color: competitionFilters == "marketsetting" ? "#cb0c9f !important" : "#17c1e8",
                }}
                disabled={filterCompetitionData.length == 0}
                onClick={(e) => filterCompetitions(e, "marketsetting")}
              >
                Market Settings
              </Button>
              <Button
                sx={{
                  display: advancedView ? "" : "none",
                  color: competitionFilters == "inplaysetting" ? "#cb0c9f !important" : "#17c1e8",
                }}
                disabled={filterCompetitionData.length == 0}
                onClick={(e) => filterCompetitions(e, "inplaysetting")}
              >
                Inplay Odd Settings
              </Button>
              <Button
                sx={{
                  display: advancedView ? "" : "none",
                  color: competitionFilters == "pregamesetting" ? "#cb0c9f !important" : "#17c1e8",
                }}
                disabled={filterCompetitionData.length == 0}
                onClick={(e) => filterCompetitions(e, "pregamesetting")}
              >
                Pregame Odd Settings
              </Button>
            </Grid>
            <Grid style={{ width: "30%", textAlign: "right" }}>
              <Button onClick={handleAdvancedView}>
                {advancedView ? "Normal View" : "Advanced View"}
              </Button>
            </Grid>
          </Grid>
          <CompetitionsTable
            provider={provider}
            template={template}
            selectedSport={selectedSportName}
            filterSportData={filterSportData}
            filterCompetitionData={filterCompetitionData}
            handleStepperButtonStatus={handleStepperButtonStatus}
            handleStepData={handleStepData}
            advancedView={advancedView}
            filters={competitionFilters}
            showCompetitionPromosSwitch={showCompetitionPromosSwitch}
          ></CompetitionsTable>
        </TableContainer>
      </SoftBox>
    </SoftBox>
  );
}

// Typechecking props for the DataTable
Competitions.propTypes = {
  provider: PropTypes.object,
  template: PropTypes.string,
  initData: PropTypes.array,
  filterSportData: PropTypes.array,
  filterCompetitionData: PropTypes.array,
  handleStepperButtonStatus: PropTypes.func,
  handleStepData: PropTypes.func,
  showSportPromosSwitch: PropTypes.bool,
  showCompetitionPromosSwitch: PropTypes.bool,
};

export default Competitions;
